import {
	CommandType,
	SenderSource,
	IChatMessage,
	IChatCommandSetLangMessage,
	isCommandMessageSurvey,
	isCommandMessageSurveyInline,
	isEventMessageSurveyData,
	isCommandMessageFormFullscreen,
	isEventMessagePostback,
	isCommandMessageFormLocked,
	isCommandMessageShowTyping,
	isCommandMessageSurveyStatus,
	isCommandSetLang,
	isCommandCloseChannel,
	CloseEventType,
} from '@enuan/chat__utils';
import { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { messagesStackSlice } from '../../reduxStore/slices';
import { frameStateContext } from '../../contexts/frame/context';
import { configContext } from 'src/contexts/config';
import { instanceContext } from 'src/contexts/instance';
import { ChannelState } from '../../interfaces/ChannelState';

const getLastIncomingMessage = (messages: IChatMessage[]) =>
	messages.find((msg) => msg.sender !== SenderSource.USER);

/**
 * Returns a stable callback that applies UI side-effects (page navigation, input lock,
 * survey toggle, locale change, channel close) based on the last incoming message.
 *
 * @returns A memoised handler accepting an array of chat messages.
 */
export const useMessageSideEffectsHandler = () => {
	const dispatch = useDispatch();
	const { togglePage } = useContext(frameStateContext);
	const { setChannelState, setSurvey, setLocale, setLockInput } =
		useContext(configContext);
	const { instance: provider } = useContext(instanceContext);

	return useCallback(
		(messages: IChatMessage[]) => {
			const message = getLastIncomingMessage(messages);

			if (
				isCommandMessageSurvey(message) &&
				!isCommandMessageSurveyInline(message) &&
				!messages?.some(
					(msg) =>
						isEventMessageSurveyData(msg) &&
						msg._data.parameters.metadata.command_id === message.metadata.id,
				)
			) {
				togglePage('survey');
			} else if (
				isCommandMessageFormFullscreen(message) &&
				!messages?.some(
					(msg) =>
						isEventMessagePostback(msg) &&
						msg._data.parameters.metadata.command_id === message.metadata.id,
				)
			) {
				togglePage('form', {
					config: message._data.parameters.data,
					message: {
						command_id: message.metadata.id,
						client_id: message.client_id,
					},
				});
			} else {
				togglePage('messages');
			}

			if (isCommandMessageFormLocked(message)) {
				setLockInput(true);
			}
			if (isCommandMessageShowTyping(message)) {
				dispatch(messagesStackSlice.actions.setAgentTypingMessage(message));
			}
			if (isCommandMessageSurveyStatus(message)) {
				const enabled = message._data.name === CommandType.SURVEY_ENABLE;
				setSurvey(enabled);
			}

			const lastSetLang = messages.find((m) => isCommandSetLang(m)) as
				| IChatCommandSetLangMessage
				| undefined;
			if (lastSetLang?._data?.parameters?.lang) {
				setLocale(lastSetLang._data.parameters.lang);
			}
			if (isCommandCloseChannel(message) && provider) {
				provider
					.close(1000, CloseEventType.CHANNEL_CLOSED)
					.then(() => {})
					.catch(console.error);
				setChannelState(ChannelState.CHANNEL_CLOSED);
			}
		},
		[
			togglePage,
			setLockInput,
			setSurvey,
			setLocale,
			setChannelState,
			dispatch,
			provider,
		],
	);
};
